import { useCallback, useMemo } from "react";
import { FilterOption } from "./types";
import { findValueByName } from "./utils";
import { useMultiSelectCombobox } from "../../core/MultiSelectComboBox";

interface SelectOption {
  id: string | number;
  label: string;
  value: string;
}

export const useSearchFilters = (
  filters?: FilterOption[],
  {
    showLookupSourceFilter = false,
    showLookupNameFilter = false,
    importedListingsImports = [],
    updateSearchParams,
  }: {
    showLookupSourceFilter?: boolean;
    showLookupNameFilter?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    importedListingsImports?: any[];
    updateSearchParams?: (updates: Record<string, unknown>) => void;
  } = {}
) => {
  
  // Derive values directly from filters prop instead of local state
  const selectedListingStatuses = useMemo(() => findValueByName(filters, "status") || [], [filters]);
  const distressedLevel = useMemo(() => findValueByName(filters, "distressedLevel") || [], [filters]);
  const preForeclosureStatus = useMemo(() => findValueByName(filters, "preForeclosureStatus") || [], [filters]);
  const propertyTypes = useMemo(() => findValueByName(filters, "propertyTypes") || [], [filters]);
  const searchDateType = findValueByName(filters, "searchDateType") || "";
  const dateSearchType = findValueByName(filters, "dateSearchType") || "custom";
  const fromDate = findValueByName(filters, "fromDate") || "";
  const toDate = findValueByName(filters, "toDate") || "";
  const mls = findValueByName(filters, "mls") || "";
  const numBeds = findValueByName(filters, "numBeds") || "";
  const numBaths = findValueByName(filters, "numBaths") || "";
  const minPrice = findValueByName(filters, "minPrice") || "";
  const maxPrice = findValueByName(filters, "maxPrice") || "";
  const commType = findValueByName(filters, "commType") || "";
  const sqFootage = findValueByName(filters, "sqFootage") || "";
  const postalCode = findValueByName(filters, "postalCode") || "";
  const showMLSActives = findValueByName(filters, "showMLSActives") === "true" || findValueByName(filters, "showMLSActives") === true;
  const tags = findValueByName(filters, "tags") || "";
  const isTagsAndSearch = findValueByName(filters, "isTagsAndSearch") === "true" || findValueByName(filters, "isTagsAndSearch") === true;
  
  // For isHideDNCPhoneNumbers, convert the filter value to boolean
  // The filter value already includes the account default from useSearchListings
  const hideDNCFilterValue = findValueByName(filters, "isHideDNCPhoneNumbers");
  const isHideDNCPhoneNumbers = hideDNCFilterValue === "true" || hideDNCFilterValue === true;

  const {
    selectedItems: lookupSources,
    handleSelect: handleSelectLookupSource,
    handleRemove: handleRemoveLookupSource,
  } = useMultiSelectCombobox({
    initialSelectedItems: (findValueByName(filters, "lookupSources") || []).map(
      (source: string) => ({ label: source, value: source, id: source })
    ),
  });

  const {
    selectedItems: lookupIds,
    handleSelect: handleSelectLookupName,
    handleRemove: handleRemoveLookupName,
  } = useMultiSelectCombobox({
    initialSelectedItems: (findValueByName(filters, "lookupIds") || []).reduce(
      (acc: SelectOption[], lookupId: string) => {
        const importedListing = importedListingsImports.find(
          ({ id }) => `${id}` === lookupId
        );

        return importedListing
          ? [
              ...acc,
              {
                id: importedListing.id,
                label: importedListing.name,
                value: importedListing.id,
              },
            ]
          : acc;
      },
      []
    ),
  });

  // Create setter functions that use updateSearchParams
  const setSelectedListingStatuses = useCallback((statuses: string[]) => {
    updateSearchParams?.({ status: statuses, currentPage: 1 });
  }, [updateSearchParams]);

  const setDistressedLevel = useCallback((levels: string[]) => {
    updateSearchParams?.({ distressedLevel: levels, currentPage: 1 });
  }, [updateSearchParams]);

  const setPreForeclosureStatus = useCallback((status: string[]) => {
    updateSearchParams?.({ preForeclosureStatus: status, currentPage: 1 });
  }, [updateSearchParams]);

  const setSearchDateType = useCallback((type: string) => {
    updateSearchParams?.({ searchDateType: type, currentPage: 1 });
  }, [updateSearchParams]);

  const setFromDate = useCallback((date: string) => {
    updateSearchParams?.({ fromDate: date, currentPage: 1 });
  }, [updateSearchParams]);

  const setToDate = useCallback((date: string) => {
    updateSearchParams?.({ toDate: date, currentPage: 1 });
  }, [updateSearchParams]);

  const setDateRange = useCallback((from: string, to: string, preset?: string) => {
    updateSearchParams?.({
      fromDate: from,
      toDate: to,
      ...(preset && { dateSearchType: preset }),
      currentPage: 1
    });
  }, [updateSearchParams]);

  const setDateSearchType = useCallback((type: string) => {
    updateSearchParams?.({ dateSearchType: type, currentPage: 1 });
  }, [updateSearchParams]);

  const setMls = useCallback((mls: string) => {
    updateSearchParams?.({ mls, currentPage: 1 });
  }, [updateSearchParams]);

  const setNumBeds = useCallback((beds: string) => {
    updateSearchParams?.({ numBeds: beds, currentPage: 1 });
  }, [updateSearchParams]);

  const setNumBaths = useCallback((baths: string) => {
    updateSearchParams?.({ numBaths: baths, currentPage: 1 });
  }, [updateSearchParams]);

  const setMinPrice = useCallback((price: number) => {
    updateSearchParams?.({ minPrice: price.toString(), currentPage: 1 });
  }, [updateSearchParams]);

  const setMaxPrice = useCallback((price: number) => {
    updateSearchParams?.({ maxPrice: price.toString(), currentPage: 1 });
  }, [updateSearchParams]);

  // Batched version to avoid race condition
  const setPriceRange = useCallback((minPrice: number, maxPrice: number) => {
    updateSearchParams?.({ 
      minPrice: minPrice.toString(), 
      maxPrice: maxPrice.toString(), 
      currentPage: 1 
    });
  }, [updateSearchParams]);

  const setCommType = useCallback((type: string) => {
    updateSearchParams?.({ commType: type, currentPage: 1 });
  }, [updateSearchParams]);

  const setSqFootage = useCallback((sqft: string) => {
    updateSearchParams?.({ sqFootage: sqft, currentPage: 1 });
  }, [updateSearchParams]);

  const setPostalCode = useCallback((code: string) => {
    updateSearchParams?.({ postalCode: code, currentPage: 1 });
  }, [updateSearchParams]);

  const setShowActives = useCallback((show: boolean) => {
    updateSearchParams?.({ showMLSActives: show, currentPage: 1 });
  }, [updateSearchParams]);

  const setTags = useCallback((tags: string) => {
    updateSearchParams?.({ tags, currentPage: 1 });
  }, [updateSearchParams]);

  const setIsTagsAndSearch = useCallback((isAndSearch: boolean) => {
    updateSearchParams?.({ isTagsAndSearch: isAndSearch, currentPage: 1 });
  }, [updateSearchParams]);

  const setIsHideDNCPhoneNumbers = useCallback((hide: boolean) => {
    updateSearchParams?.({ isHideDNCPhoneNumbers: hide });
  }, [updateSearchParams]);

  const setPropertyTypes = useCallback((types: string[]) => {
    updateSearchParams?.({ propertyTypes: types, currentPage: 1 });
  }, [updateSearchParams]);

  const filterValues = useMemo(() => {
    return {
      fromDate,
      toDate,
      minPrice,
      maxPrice,
      numBeds,
      numBaths,
      sqFootage,
      postalCode,
      mls,
      searchDateType,
      tags,
      status: selectedListingStatuses,
      showMLSActives,
      isHideDNCPhoneNumbers,
      distressedLevel,
      preForeclosureStatus,
      commType,
      ...(showLookupSourceFilter && {
        lookupSources: lookupSources.map((item) => item.value),
      }),
      ...(showLookupNameFilter && {
        lookupIds: lookupIds.map((item) => item.value),
      }),
    };
  }, [
    selectedListingStatuses,
    distressedLevel,
    preForeclosureStatus,
    searchDateType,
    fromDate,
    toDate,
    mls,
    numBeds,
    numBaths,
    minPrice,
    maxPrice,
    commType,
    sqFootage,
    postalCode,
    showMLSActives,
    isHideDNCPhoneNumbers,
    tags,
    showLookupSourceFilter,
    showLookupNameFilter,
    lookupSources,
    lookupIds,
  ]);

  return {
    filterValues,
    selectedListingStatuses,
    setSelectedListingStatuses,
    distressedLevel,
    setDistressedLevel,
    preForeclosureStatus,
    setPreForeclosureStatus,
    searchDateType,
    setSearchDateType,
    dateSearchType,
    setDateSearchType,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    setDateRange,
    mls,
    setMls,
    numBeds,
    setNumBeds,
    numBaths,
    setNumBaths,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    setPriceRange,
    commType,
    setCommType,
    sqFootage,
    setSqFootage,
    postalCode,
    setPostalCode,
    showMLSActives,
    setShowActives,
    tags,
    setTags,
    isTagsAndSearch,
    setIsTagsAndSearch,
    isHideDNCPhoneNumbers,
    setIsHideDNCPhoneNumbers,
    propertyTypes,
    setPropertyTypes,
    lookupSources: lookupSources as SelectOption[],
    handleSelectLookupSource,
    handleRemoveLookupSource,
    lookupIds: lookupIds as SelectOption[],
    handleSelectLookupName,
    handleRemoveLookupName,
    handleOnComplete: () => {
      return {
        fromDate,
        toDate,
        minPrice,
        maxPrice,
        numBeds,
        numBaths,
        sqFootage,
        postalCode,
        mls,
        searchDateType,
        tags,
        status: selectedListingStatuses,
        showMLSActives,
        isHideDNCPhoneNumbers,
        distressedLevel,
        preForeclosureStatus,
        commType,
        ...(showLookupSourceFilter && {
          lookupSources: lookupSources.map((item) => item.value),
        }),
        ...(showLookupNameFilter && {
          lookupIds: lookupIds.map((item) => item.value),
        }),
      };
    },
  };
};
